/* eslint-disable @typescript-eslint/no-explicit-any */
import { WIDGET_IDS, WIDGET_NAMES } from "@/constants/widget";
import { EMPTY_AGENT_ID } from "@/constants/queue";
import { getClientTimeZone } from "@/utils/dateUtils";
import axiosInstance from '../config/axiosConfig';
import realtimeAxiosInstance from '../config/realtimeAxiosConfig';
import {
  FetchWidgetsParams,
  WidgetsApiResponse
} from '../types/widgets.type';
import { PaginatedResponse } from '../types';
import { StatusOverTimeRow } from '../types/widgets.type';
import type {
  WidgetsTableRow,
  UserPerformanceTableRow,
  AgentPerformance,
  AgentPerQueueRow,
  AgentJourneyEvent,
  AgentCallDetail,
  AgentCallDetailsPage,
  InboundHandledByQueueRow,
} from '../types/widgets.type';

export async function fetchWidgetsData({
  startDate,
  endDate,
  previousStartDate,
  previousEndDate,
  queueMicrosoftIds,
  agentMicrosoftIds,
  groupIds,
  widgetIds,
  specificDateRanges,
  callId,
  widgetPagination,
  widgetSort
}: FetchWidgetsParams, options?: { signal?: AbortSignal }): Promise<WidgetsApiResponse> {
  const widgets: {
    widgetId: number;
    queueMicrosoftIds?: string[];
    agentMicrosoftIds?: string[];
    groupIds?: number[];
    previousEndDate?: string;
    previousStartDate?: string;
    startDate?: string;
    endDate?: string;
    callId?: string;
    page?: number;
    limit?: number;
    sort?: 'asc' | 'desc';
  }[] = [
      { widgetId: WIDGET_IDS.TOTAL_INBOUND_CALLS, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_OUTBOUND_CALLS, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_INBOUND_CALLS_HANDLED, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_INBOUND_CALLS_MISSED, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_OUTBOUND_CALLS_HANDLED, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_OUTBOUND_CALLS_MISSED, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_INTERNAL_CALLS, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_INBOUND_DIRECT_CALLS, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_ABANDONED_CALLS, queueMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_INBOUND_TRANSFERRED_CALLS, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_TRANSFERRED_CALLS, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_FORWARDED_CALLS, queueMicrosoftIds, agentMicrosoftIds, groupIds },
      { widgetId: WIDGET_IDS.TRANSFERRED_RATE, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.SLA, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.CALL_ABANDONED_RATE, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_WAITING_TIME, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_HANDLE_TIME, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_SPEED_OF_ANSWER, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_ACTIVE_AGENTS, queueMicrosoftIds, agentMicrosoftIds, groupIds },
      { widgetId: WIDGET_IDS.TOP_PERFORMER, queueMicrosoftIds, agentMicrosoftIds, groupIds },
      { widgetId: WIDGET_IDS.AVERAGE_CALLS_PER_AGENT, queueMicrosoftIds, agentMicrosoftIds, groupIds },
      { widgetId: WIDGET_IDS.CALL_VOLUME, queueMicrosoftIds, agentMicrosoftIds, groupIds },
      { widgetId: WIDGET_IDS.INBOUND_DIRECT_STATUS_DISTRIBUTION, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.OUTBOUND_STATUS_DISTRIBUTION, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.CALLS_HANDLED_BY_QUEUE, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.SLA_RANKING, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate, ...(widgetSort?.[WIDGET_IDS.SLA_RANKING] ? { sort: widgetSort[WIDGET_IDS.SLA_RANKING] } : {}) },
      { widgetId: WIDGET_IDS.STATUS_OVER_TIME, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.CALL_VOLUME_TREND, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.CALL_STATUS_DISTRIBUTION_TREND, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_HANDLING_TIME_TREND, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_WAITING_TIME_TREND, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_WAITING_TIME_TREND_INBOUND_DIRECT, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_HANDLING_TIME_TREND_INBOUND_DIRECT, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_SPEED_OF_ANSWER_INBOUND_DIRECT, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.SLA_TREND, queueMicrosoftIds, agentMicrosoftIds, groupIds },
      { widgetId: WIDGET_IDS.SLA_BY_CALL_QUEUE, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.CALL_DETAIL_OVERVIEW, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AGENT_PERFORMANCE, queueMicrosoftIds, agentMicrosoftIds, groupIds },
      { widgetId: WIDGET_IDS.CALL_JOURNEY, callId },
      { widgetId: WIDGET_IDS.AVERAGE_CALLS_BY_AGENTS, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.CALL_VOLUME_DISTRIBUTION, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_CALLS_PER_AGENT_CQ, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
    ];

  specificDateRanges?.forEach(specificDateRange => {
    const needToUpdateWidgets = widgets.filter(widget => specificDateRange.widgetIds.includes(widget.widgetId));
    needToUpdateWidgets.forEach(widget => {
      widget.startDate = specificDateRange.startDate;
      widget.endDate = specificDateRange.endDate;
      widget.previousStartDate = specificDateRange.previousStartDate;
      widget.previousEndDate = specificDateRange.previousEndDate;
    });
  });

  // Apply per-widget pagination if provided
  if (widgetPagination) {
    widgets.forEach(w => {
      const p = widgetPagination[w.widgetId];
      if (p) {
        w.page = p.page;
        w.limit = p.limit;
      }
    });
  }

  // Apply per-widget sort if provided
  if (widgetSort) {
    widgets.forEach(w => {
      const s = widgetSort[w.widgetId];
      if (s) {
        w.sort = s as 'asc' | 'desc';
      }
    });
  }

  const payload = {
    startDate,
    endDate,
    widgets: widgets.filter((widget) => widgetIds.includes(widget.widgetId)),
  };

  const response = await axiosInstance.post<{ data: { widgetData: any[] } }>(
    `/widgets/data`,
    payload,
    { signal: options?.signal }
  );
  const widgetsData = response.data?.data?.widgetData || [];

  // Helper to get widget result by name
  const getWidget = (name: string) => widgetsData.find((w: any) => w.widgetName === name)?.result;

  // Helper to group slaTrend data by time
  const groupSlaTrendData = (data: any[] | undefined): any[] => {
    if (!data || !Array.isArray(data)) return [];

    const grouped = data.reduce((acc, item) => {
      const timeKey = item.time;
      if (!acc[timeKey]) {
        acc[timeKey] = { time: timeKey };
      }

      // Merge all properties from the item
      Object.keys(item).forEach(key => {
        if (key !== 'time') {
          acc[timeKey][key] = item[key];
        }
      });

      return acc;
    }, {} as Record<string, any>);

    return (Object.values(grouped) as any[]).sort((a: any, b: any) => new Date(a.time).getTime() - new Date(b.time).getTime());
  };

  // Map REST response to WidgetsApiResponse
  const mapped: WidgetsApiResponse = {
    totalAgents: getWidget('TotalActiveAgents')?.totalActiveAgents ?? 0,
    comparisonPercentageTAW: getWidget('TotalActiveAgents')?.comparisonPercentage ?? null,
    topPerformer: getWidget('TopPerformer')?.agentName ?? '',
    averageCallsPerAgent: getWidget('AverageCallsPerAgent')?.averageCalls ?? 0,
    comparisonPercentageACP: ((getWidget('AverageCallsPerAgent') as { data?: Array<{ averageCalls?: number }> } | undefined)?.data?.reduce((sum, item) => sum + (item.averageCalls ?? 0), 0) ?? 0) / 100,
    averageCallsPerAgentCQ: getWidget('AverageCallsPerAgentCallQueueInsight')?.averageCalls ?? 0,
    averageHandlingTime: getWidget('AverageHandleTime')?.averageHandleTime ?? 0,
    comparisonPercentageAHT: getWidget('AverageHandleTime')?.comparisonPercentage ?? null,
    averageSpeedOfAnswer: getWidget('AverageSpeedOfAnswer')?.averageSpeedOfAnswer ?? 0,
    comparisonPercentageASO: getWidget('AverageSpeedOfAnswer')?.comparisonPercentage ?? null,
    averageSpeedOfAnswerInboundDirect: getWidget('AverageSpeedOfAnswerInboundDirect')?.averageSpeedOfAnswer ?? 0,
    comparisonPercentageASOInboundDirect: getWidget('AverageSpeedOfAnswerInboundDirect')?.comparisonPercentage ?? null,
    averageWaitingTime: getWidget('AverageWaitingTime')?.averageWaitingTime ?? 0,
    comparisonPercentageAWT: getWidget('AverageWaitingTime')?.comparisonPercentage ?? null,
    averageWaitingTimeInboundDirect: getWidget('AverageWaitingTimeInboundDirect')?.averageWaitingTime ?? 0,
    comparisonPercentageAWTInboundDirect: getWidget('AverageWaitingTimeInboundDirect')?.comparisonPercentage ?? null,
    averageHandlingTimeInboundDirect: getWidget('AverageHandleTimeInboundDirect')?.averageHandleTime ?? 0,
    comparisonPercentageAHTInboundDirect: getWidget('AverageHandleTimeInboundDirect')?.comparisonPercentage ?? null,
    slaRanking: (((getWidget('SLARanking') as { data?: Array<{ agentName: string; slaPercentage: number }> } | undefined)?.data) ?? []).map((item) => ({
      agent: item.agentName,
      sla: item.slaPercentage,
    })),
    statusOverTime: (((getWidget('StatusOverTime') as { data?: Array<{ agentName: string } & Record<string, unknown>> } | undefined)?.data) ?? []).map((item) => {
      const { agentName, ...statusFields } = item as { agentName: string } & Record<string, unknown>;
      return {
        agent: agentName,
        agentName,
        ...statusFields,
      };
    }),
    agentPerformanceTable: (((getWidget('AgentPerformance') as { data?: Array<Record<string, unknown>> } | undefined)?.data) ?? []).map((item, idx: number) => ({
      key: idx,
      agent: (item as any).agentName,
      agentName: (item as any).agentName ?? '',
      totalInboundCallsHandled: (item as any).totalInboundCallsHandled ?? 0,
      totalInboundCallsMissed: (item as any).totalInboundCallsMissed ?? (item as any).inboundMissed ?? 0,
      totalOutboundCallsHandled: (item as any).totalOutboundCallsHandled ?? 0,
      totalOutboundCallsMissed: (item as any).totalOutboundCallsMissed ?? (item as any).outboundMissed ?? 0,
      totalInboundDirectCallsHandled: (item as any).totalInboundDirectCallsHandled ?? 0,
      totalInboundDirectCallsMissed: (item as any).totalInboundDirectCallsMissed ?? 0,
      totalMissedDirectCalls: (item as any).totalMissedDirectCalls ?? 0, // legacy
      totalForwarded: (item as any).totalForwarded ?? 0, // legacy
      totalTransferred: (item as any).totalTransferred ?? 0, // legacy
      totalTransferredCalls: (item as any).totalTransferredCalls ?? (item as any).totalTransferred ?? 0,
      totalInternal: (item as any).totalInternal ?? 0, // legacy
      totalInternalCalls: (item as any).totalInternalCalls ?? (item as any).totalInternal ?? 0,
      totalVoicemailCalls: (item as any).voicemail ?? (item as any).totalVoicemailCalls ?? 0,
      totalMissedCalls: (item as any).totalMissedCalls ?? 0, // legacy
      averageHandledTime: (item as any).averageHandledTime ?? 0,
      averageWaitingTime: (item as any).averageWaitingTime ?? 0,
      averageSpeedOfAnswer: (item as any).averageSpeedOfAnswer ?? 0,
      slaPercentage: (item as any).sla?.toString() ?? '',
      callQueue: (item as any).callQueue ?? '',
      inbound: (item as any).totalInboundCallsHandled ?? 0,
      outbound: (item as any).totalOutboundCallsHandled ?? 0,
      missed: (item as any).totalMissedCalls ?? 0,
      sla: Number((item as any).sla ?? 0),
    })),
    trendsInsightsData: {
      callVolumeTrend: getWidget('CallVolumeTrend')?.data ?? null,
      callStatusDistributionTrend: getWidget('CallStatusDistributionTrend')?.data ?? null,
      averageHandlingTimeTrend: getWidget('AverageHandlingTimeTrend')?.data ?? null,
      averageWaitingTimeTrend: getWidget('AverageWaitingTimeTrend')?.data ?? null,
      averageCallsPerAgentTrend: getWidget('AverageCallsPerAgentTrend')?.data ?? null,
      slaTrend: groupSlaTrendData((getWidget('SLATrend') as { data?: Array<{ time: string;[k: string]: unknown }> } | undefined)?.data) as unknown as any,
      slaByCallQueue: getWidget('SLAByCallQueue')?.data ?? null,
    },
    callOverviewData: {
      totalInboundCalls: getWidget(WIDGET_NAMES.TOTAL_INBOUND_CALLS),
      totalInboundDirectCalls: getWidget(WIDGET_NAMES.TOTAL_INBOUND_DIRECT_CALLS),
      totalOutboundCalls: getWidget(WIDGET_NAMES.TOTAL_OUTBOUND_CALLS),
      totalInboundCallsHandled: getWidget(WIDGET_NAMES.TOTAL_INBOUND_CALLS_HANDLED),
      totalInboundCallsMissed: getWidget(WIDGET_NAMES.TOTAL_INBOUND_CALLS_MISSED),
      totalOutboundCallsHandled: getWidget(WIDGET_NAMES.TOTAL_OUTBOUND_CALLS_HANDLED),
      totalOutboundCallsMissed: getWidget(WIDGET_NAMES.TOTAL_OUTBOUND_CALLS_MISSED),
      totalInternalCalls: getWidget(WIDGET_NAMES.TOTAL_INTERNAL_CALLS),
      totalAbandonedCalls: getWidget(WIDGET_NAMES.TOTAL_ABANDONED_CALLS),
      totalTransferredCalls: getWidget(WIDGET_NAMES.TOTAL_TRANSFERRED_CALLS),
      totalInboundTransferredCalls: getWidget(WIDGET_NAMES.TOTAL_INBOUND_TRANSFERRED_CALLS),
      totalForwardedCalls: getWidget(WIDGET_NAMES.TOTAL_FORWARDED_CALLS),
      transferredRate: getWidget(WIDGET_NAMES.TRANSFERRED_RATE),
      sla: getWidget(WIDGET_NAMES.SLA),
      callAbandonedRate: getWidget(WIDGET_NAMES.CALL_ABANDONED_RATE),
      averageWaitingTime: getWidget(WIDGET_NAMES.AVERAGE_WAITING_TIME),
      averageHandleTime: getWidget(WIDGET_NAMES.AVERAGE_HANDLE_TIME),
      averageSpeedOfAnswer: getWidget(WIDGET_NAMES.AVERAGE_SPEED_OF_ANSWER),
      inboundDirectStatusDistribution: getWidget(WIDGET_NAMES.INBOUND_DIRECT_STATUS_DISTRIBUTION),
      outboundStatusDistribution: getWidget(WIDGET_NAMES.OUTBOUND_STATUS_DISTRIBUTION),
      callsHandledByQueue: getWidget(WIDGET_NAMES.CALLS_HANDLED_BY_QUEUE)?.data ?? [],
      callVolume: getWidget(WIDGET_NAMES.CALL_VOLUME)?.data ?? [],
      callDetailOverview: getWidget(WIDGET_NAMES.CALL_DETAIL_OVERVIEW)?.data ?? [],
      callVolumeDistribution: getWidget(WIDGET_NAMES.CALL_VOLUME_DISTRIBUTION)?.data ?? [],
    },
    callJourney: getWidget('CallJourney')?.callJourneyDetailDtos ?? []
  };

  // If server returns paginated payloads for specific widgets, map them as well
  const slaRankingRaw = widgetsData.find((w: { widgetName: string }) => w.widgetName === WIDGET_NAMES.SLA_RANKING)?.result as { data?: any[]; total?: number; page?: number; limit?: number; totalPages?: number } | undefined;
  if (slaRankingRaw && slaRankingRaw?.data && typeof slaRankingRaw?.total === 'number') {
    mapped.slaRankingPaged = {
      data: (slaRankingRaw.data || []).map((item: { agentName: string; slaPercentage: number }) => ({ agent: item.agentName, sla: item.slaPercentage })),
      total: slaRankingRaw.total,
      page: slaRankingRaw.page ?? 1,
      limit: slaRankingRaw.limit ?? (widgetPagination?.[WIDGET_IDS.SLA_RANKING]?.limit || 10),
      totalPages: slaRankingRaw.totalPages ?? Math.ceil((slaRankingRaw.total || 0) / (slaRankingRaw.limit || 10))
    };
  }

  const statusOverTimeRaw = widgetsData.find((w: { widgetName: string }) => w.widgetName === WIDGET_NAMES.STATUS_OVER_TIME)?.result as { data?: any[]; total?: number; page?: number; limit?: number; totalPages?: number } | undefined;
  if (statusOverTimeRaw && statusOverTimeRaw?.data && typeof statusOverTimeRaw?.total === 'number') {
    mapped.statusOverTimePaged = {
      data: (statusOverTimeRaw.data || []).map((item: Record<string, unknown>) => {
        const { agentName, ...statusFields } = item as { agentName: string } & Record<string, unknown>;
        return {
          agent: agentName,
          agentName,
          ...statusFields,
        };
      }),
      total: statusOverTimeRaw.total,
      page: statusOverTimeRaw.page ?? 1,
      limit: statusOverTimeRaw.limit ?? (widgetPagination?.[WIDGET_IDS.STATUS_OVER_TIME]?.limit || 10),
      totalPages: statusOverTimeRaw.totalPages ?? Math.ceil((statusOverTimeRaw.total || 0) / (statusOverTimeRaw.limit || 10))
    };
  }

  return mapped;
}

export async function fetchAgentPerformanceTable(params: {
  startTime: string;
  endTime: string;
  agentMicrosoftIds: string[] | null;
  agentNameSearchText?: string | null;
  sortTable?: { columnName: string; columnValue: 'asc' | 'desc' } | null;
  page?: number | null;
  limit?: number | null;
}, options?: { signal?: AbortSignal }): Promise<{ data: WidgetsTableRow[]; total: number }> {
  const payload = {
    startTime: params.startTime,
    endTime: params.endTime,
    agentMicrosoftIds: params.agentMicrosoftIds,
    agentNameSearchText: params.agentNameSearchText ?? null,
    sortTable: params.sortTable ?? undefined,
    page: params.page ?? 1,
    limit: params.limit ?? null,
  };
  const res = await axiosInstance.post<{ data: any[]; total?: number }>(`/widgets/agent-performance`, payload, { signal: options?.signal });
  const rows = (res.data?.data ?? []).map((item, idx: number) => ({
    key: idx,
    agent: (item as any).agentName,
    agentName: (item as any).agentName ?? '',
    totalInboundCallsHandled: (item as any).totalInboundCallsHandled ?? 0,
    totalInboundCallsMissed: (item as any).totalInboundCallsMissed ?? 0,
    totalOutboundCallsHandled: (item as any).totalOutboundCallsHandled ?? 0,
    totalOutboundCallsMissed: (item as any).totalOutboundCallsMissed ?? 0,
    totalInboundDirectCallsHandled: (item as any).totalInboundDirectCallsHandled ?? 0,
    totalInboundDirectCallsMissed: (item as any).totalInboundDirectCallsMissed ?? 0,
    totalTransferredCalls: (item as any).totalTransferredCalls ?? (item as any).totalTransferred ?? 0,
    totalInternalCalls: (item as any).totalInternalCalls ?? (item as any).totalInternal ?? 0,
    totalVoicemailCalls: (item as any).voicemail ?? (item as any).totalVoicemailCalls ?? 0,
    averageHandledTime: (item as any).averageHandledTime ?? 0,
    averageWaitingTime: (item as any).averageWaitingTime ?? 0,
    averageSpeedOfAnswer: (item as any).averageSpeedOfAnswer ?? 0,
    sla: Number((item as any).sla ?? 0),
    callQueue: (item as any).callQueue ?? '',
    totalMissedCalls: (item as any).totalInboundCallsMissed ?? 0,
    totalMissedDirectCalls: (item as any).totalInboundDirectCallsMissed ?? 0,
    totalForwarded: (item as any).totalForwarded ?? 0,
    totalTransferred: (item as any).totalTransferred ?? 0,
    totalInternal: (item as any).totalInternal ?? 0,
  })) as WidgetsTableRow[];
  return { data: rows, total: res.data?.total ?? rows.length };
}

// Dedicated endpoint: SLA Ranking (server-side pagination, reload only this widget)
export async function fetchSlaRankingPaged({
  startDate,
  endDate,
  previousStartDate,
  previousEndDate,
  queueMicrosoftIds,
  agentMicrosoftIds,
  groupIds,
  page,
  limit,
  sort
}: {
  startDate: string;
  endDate: string;
  previousStartDate?: string;
  previousEndDate?: string;
  queueMicrosoftIds?: string[] | null;
  agentMicrosoftIds?: string[] | null;
  groupIds?: number[] | null;
  page: number;
  limit: number;
  sort?: 'asc' | 'desc';
}, options?: { signal?: AbortSignal }): Promise<PaginatedResponse<{ agent: string; sla: number }>> {
  const payload = {
    startDate,
    endDate,
    widgets: [
      {
        widgetId: WIDGET_IDS.SLA_RANKING,
        queueMicrosoftIds: queueMicrosoftIds ?? undefined,
        agentMicrosoftIds: agentMicrosoftIds ?? undefined,
        groupIds: groupIds ?? undefined,
        previousStartDate,
        previousEndDate,
        page,
        limit,
        ...(sort ? { sort } : {})
      }
    ]
  };

  const res = await axiosInstance.post<any>(`/widgets/sla-ranking`, payload, { signal: options?.signal });
  // Normalize various response shapes: { data: [...] , total } or { data: { data: [...], total, ... } } or { result: { ... } }
  const top = res?.data ?? {};
  const container = (top?.data && !Array.isArray(top.data)) ? top.data : (top?.result && !Array.isArray(top.result)) ? top.result : top;
  const rawData: any[] = Array.isArray(top?.data)
    ? top.data
    : Array.isArray(container?.data)
      ? container.data
      : [];
  const mappedData = (rawData || []).map((item: any) => ({
    agent: item.agentName ?? item.agent ?? '',
    sla: item.slaPercentage ?? item.sla ?? 0
  }));
  const total = Number(container?.total ?? top?.total ?? 0);
  const resp: PaginatedResponse<{ agent: string; sla: number }> = {
    data: mappedData,
    total,
    page: Number(container?.page ?? top?.page ?? page ?? 1),
    limit: Number(container?.limit ?? top?.limit ?? limit ?? 10),
    totalPages: Number(container?.totalPages ?? top?.totalPages ?? Math.ceil((total || 0) / (Number(container?.limit ?? top?.limit ?? limit ?? 10))))
  };
  return resp;
}

export interface UserSlaRankingData {
  avgSla: number;
  agents: { agentName: string; sla: number }[];
  totalAgent: number;
  agentNeedAttention: number;
  unfilteredNumber: number;
  /** TCX-fix-role: pairs with unfilteredNumber — pinned to the caller's full resolved team, unlike
   *  agentNeedAttention which narrows with the manual agent filter. Falls back to agentNeedAttention
   *  when the backend omits it (older/pre-fix responses). */
  unfilteredAgentNeedAttention: number;
}

export const USER_ACTIVITIES_WIDGET_ID = 9;

export interface UserActivityRow {
  agent: string;
  inbound: number;
  outbound: number;
  direct: number;
  internal: number;
}

export interface UserActivitiesData {
  totalAgent: number;
  agents: UserActivityRow[];
}

function mapUserActivityAgent(raw: Record<string, unknown>): UserActivityRow {
  return {
    agent: String(raw.agentName ?? ''),
    inbound: Number(raw.inbound ?? 0),
    outbound: Number(raw.outbound ?? 0),
    direct: Number(raw.inboundDirect ?? 0),
    internal: Number(raw.internal ?? 0),
  };
}

function resolveUserActivityAgentIds(agentMicrosoftIds?: string[] | null): string[] | null {
  return agentMicrosoftIds?.length ? agentMicrosoftIds : null;
}

// FE column key → BE field name mapping for /user/table and /user/filter
const USER_TABLE_COL: Record<string, string> = {
  userName: 'name',
  totalOffered: 'totalOffered',
  aht: 'ahtSeconds',
  transferredPct: 'transferRate',
  sla: 'slaPercent',
  lastCallTaken: 'lastCallTakenAt',
};

// User tab: Total Users widget (widgetId=0)
export async function fetchUserTotalWidget({
  startDate,
  endDate,
  agentMicrosoftIds,
  queueMicrosoftIds,
}: {
  startDate: string;
  endDate: string;
  agentMicrosoftIds?: string[] | null;
  queueMicrosoftIds?: string[] | null;
}, options?: { signal?: AbortSignal }): Promise<number | null> {
  const res = await realtimeAxiosInstance.post<any>(
    '/user/widgets',
    {
      startTime: startDate,
      endTime: endDate,
      items: [{ widgetId: 0, agentIds: agentMicrosoftIds ?? [], queueMicrosoftIds: queueMicrosoftIds ?? undefined }],
    },
    { signal: options?.signal }
  );
  const payload = (res?.data?.items as any[])?.find((i: any) => i.widgetId === 0)?.payload ?? {};
  return payload.value != null ? Number(payload.value) : null;
}

// User tab: Average SLA widget (widgetId=6)
export async function fetchUserAvgSlaWidget({
  startDate,
  endDate,
  agentMicrosoftIds,
  queueMicrosoftIds,
}: {
  startDate: string;
  endDate: string;
  agentMicrosoftIds?: string[] | null;
  queueMicrosoftIds?: string[] | null;
}, options?: { signal?: AbortSignal }): Promise<number | null> {
  const res = await realtimeAxiosInstance.post<any>(
    '/user/widgets',
    {
      startTime: startDate,
      endTime: endDate,
      items: [{ widgetId: 6, agentIds: agentMicrosoftIds ?? [], queueMicrosoftIds: queueMicrosoftIds ?? undefined }],
    },
    { signal: options?.signal }
  );
  const payload = (res?.data?.items as any[])?.find((i: any) => i.widgetId === 6)?.payload ?? {};
  return payload.avgSla != null ? Number(payload.avgSla) : null;
}

// User tab: Calls Handled widget (widgetId=3)
export async function fetchUserCallsHandledWidget({
  startDate,
  endDate,
  agentMicrosoftIds,
  queueMicrosoftIds,
}: {
  startDate: string;
  endDate: string;
  agentMicrosoftIds?: string[] | null;
  queueMicrosoftIds?: string[] | null;
}, options?: { signal?: AbortSignal }): Promise<{ handled: number; missed: number; missedPct: number } | null> {
  const res = await realtimeAxiosInstance.post<any>(
    '/user/widgets',
    {
      startTime: startDate,
      endTime: endDate,
      items: [{ widgetId: 3, agentIds: agentMicrosoftIds ?? [], queueMicrosoftIds: queueMicrosoftIds ?? undefined }],
    },
    { signal: options?.signal }
  );
  const payload = (res?.data?.items as any[])?.find((i: any) => i.widgetId === 3)?.payload ?? {};
  const handled = Number(payload.handled ?? payload.totalHandled ?? payload.callsHandled ?? 0);
  const missed = Number(payload.missed ?? payload.totalMissed ?? payload.missedCalls ?? 0);
  const total = handled + missed;
  const missedPct = total > 0 ? parseFloat(((missed / total) * 100).toFixed(2)) : 0;
  return { handled, missed, missedPct };
}

// User tab: SLA Ranking widget (widgetId=5 in user/widgets bundle)
export async function fetchUserSlaRankingData({
  startDate,
  endDate,
  agentMicrosoftIds,
  queueMicrosoftIds,
  resolvedAgentMicrosoftIds,
}: {
  startDate: string;
  endDate: string;
  agentMicrosoftIds?: string[] | null;
  queueMicrosoftIds?: string[] | null;
  resolvedAgentMicrosoftIds?: string[] | null;
}, options?: { signal?: AbortSignal }): Promise<UserSlaRankingData> {
  const res = await realtimeAxiosInstance.post<any>(
    '/user/widgets',
    {
      startTime: startDate,
      endTime: endDate,
      items: [{
        widgetId: 5,
        agentIds: agentMicrosoftIds ?? [],
        queueMicrosoftIds: queueMicrosoftIds ?? undefined,
        resolvedAgentIds: resolvedAgentMicrosoftIds ?? undefined,
      }],
    },
    { signal: options?.signal }
  );
  const payload = (res?.data?.items as any[])?.find((i: any) => i.widgetId === 5)?.payload ?? {};
  const totalAgent = Number(payload.totalAgent ?? 0);
  const agentNeedAttention = Number(payload.agentNeedAttention ?? 0);
  return {
    avgSla: Number(payload.avgSla ?? 0),
    agents: Array.isArray(payload.agents) ? payload.agents : [],
    totalAgent,
    agentNeedAttention,
    unfilteredNumber: payload.unfilteredNumber != null ? Number(payload.unfilteredNumber) : totalAgent,
    unfilteredAgentNeedAttention:
      payload.unfilteredAgentNeedAttention != null ? Number(payload.unfilteredAgentNeedAttention) : agentNeedAttention,
  };
}

export async function fetchUserActivities({
  startDate,
  endDate,
  agentMicrosoftIds,
  queueMicrosoftIds,
  pageSize,
}: {
  startDate: string;
  endDate: string;
  agentMicrosoftIds?: string[] | null;
  queueMicrosoftIds?: string[] | null;
  pageSize: number;
}, options?: { signal?: AbortSignal }): Promise<UserActivitiesData> {
  const res = await realtimeAxiosInstance.post<any>(
    '/user/widgets',
    {
      startTime: startDate,
      endTime: endDate,
      items: [{
        widgetId: USER_ACTIVITIES_WIDGET_ID,
        agentIds: resolveUserActivityAgentIds(agentMicrosoftIds),
        queueMicrosoftIds: queueMicrosoftIds ?? undefined,
        pageSize,
      }],
    },
    { signal: options?.signal }
  );
  const payload = (res?.data?.items as any[])?.find(
    (i: any) => i.widgetId === USER_ACTIVITIES_WIDGET_ID
  )?.payload ?? {};
  const agents = Array.isArray(payload.agents) ? payload.agents : [];
  return {
    totalAgent: Number(payload.totalAgent ?? 0),
    agents: agents.map((item: Record<string, unknown>) => mapUserActivityAgent(item)),
  };
}

export interface UserWidgetsBatchResult {
  totalUsers: number | null;
  callsHandled: { handled: number; missed: number; missedPct: number };
  slaRanking: UserSlaRankingData;
  avgSla: number | null;
  avgHandleTime: number | null;
}

// User tab: batch fetch all KPI widgets in one request (widgetIds 0, 3, 4, 5, 6)
export async function fetchUserWidgetsBatch({
  startDate,
  endDate,
  agentMicrosoftIds,
  queueMicrosoftIds,
  resolvedAgentMicrosoftIds,
  allAgentsSelected,
}: {
  startDate: string;
  endDate: string;
  agentMicrosoftIds?: string[] | null;
  queueMicrosoftIds?: string[] | null;
  resolvedAgentMicrosoftIds?: string[] | null;
  /**
   * SLA Ranking (5)/Average SLA (6) only, mirrors Direct Call's own `allAgentsSelected` (TCX-6503):
   * the caller stating outright that no manual agent filter is applied, rather than letting the
   * backend infer it from `agentMicrosoftIds` being non-empty — a Manager's resolved agentIds is
   * ALWAYS populated (their full team), whether or not they've narrowed it, so that alone can't
   * distinguish "viewing whole team" from "narrowed to fewer agents". `true` ⇒ the backend
   * substitutes `resolvedAgentMicrosoftIds` in place of the manual filter for both widgets' scope.
   */
  allAgentsSelected?: boolean;
}, options?: { signal?: AbortSignal }): Promise<UserWidgetsBatchResult> {
  const slaScopeFields = {
    resolvedAgentIds: resolvedAgentMicrosoftIds ?? undefined,
    allAgentsSelected,
  };
  const res = await realtimeAxiosInstance.post<any>(
    '/user/widgets',
    {
      startTime: startDate,
      endTime: endDate,
      items: [
        { widgetId: 0, agentIds: agentMicrosoftIds ?? [], queueMicrosoftIds: queueMicrosoftIds ?? undefined },
        { widgetId: 3, agentIds: agentMicrosoftIds ?? [], queueMicrosoftIds: queueMicrosoftIds ?? undefined },
        { widgetId: 4, agentIds: agentMicrosoftIds ?? [], queueMicrosoftIds: queueMicrosoftIds ?? undefined },
        {
          widgetId: 5,
          agentIds: agentMicrosoftIds ?? [],
          queueMicrosoftIds: queueMicrosoftIds ?? undefined,
          ...slaScopeFields,
        },
        {
          widgetId: 6,
          agentIds: agentMicrosoftIds ?? [],
          queueMicrosoftIds: queueMicrosoftIds ?? undefined,
          ...slaScopeFields,
        },
      ],
    },
    { signal: options?.signal }
  );
  const items: any[] = res?.data?.items ?? [];
  const getPayload = (id: number) => items.find((i: any) => i.widgetId === id)?.payload ?? {};

  const p0 = getPayload(0);
  const p3 = getPayload(3);
  const p4 = getPayload(4);
  const p5 = getPayload(5);
  const p6 = getPayload(6);

  const handled = Number(p3.handled ?? p3.totalHandled ?? p3.callsHandled ?? 0);
  const missed = Number(p3.missed ?? p3.totalMissed ?? p3.missedCalls ?? 0);
  const total3 = handled + missed;
  const missedPct = total3 > 0 ? parseFloat(((missed / total3) * 100).toFixed(2)) : 0;

  return {
    totalUsers: p0.value != null ? Number(p0.value) : null,
    callsHandled: { handled, missed, missedPct },
    slaRanking: {
      avgSla: Number(p5.avgSla ?? 0),
      agents: Array.isArray(p5.agents) ? p5.agents : [],
      totalAgent: Number(p5.totalAgent ?? 0),
      agentNeedAttention: Number(p5.agentNeedAttention ?? 0),
      unfilteredNumber: p5.unfilteredNumber != null ? Number(p5.unfilteredNumber) : Number(p5.totalAgent ?? 0),
      unfilteredAgentNeedAttention:
        p5.unfilteredAgentNeedAttention != null
          ? Number(p5.unfilteredAgentNeedAttention)
          : Number(p5.agentNeedAttention ?? 0),
    },
    avgSla: p6.avgSla != null ? Number(p6.avgSla) : null,
    avgHandleTime: p4.value != null ? Number(p4.value) : null,
  };
}

// Dedicated endpoint: Status Over Time (server-side pagination, reload only this widget)
export async function fetchStatusOverTimePaged({
  startDate,
  endDate,
  previousStartDate,
  previousEndDate,
  queueMicrosoftIds,
  agentMicrosoftIds,
  groupIds,
  page,
  limit
}: {
  startDate: string;
  endDate: string;
  previousStartDate?: string;
  previousEndDate?: string;
  queueMicrosoftIds?: string[] | null;
  agentMicrosoftIds?: string[] | null;
  groupIds?: number[] | null;
  page: number;
  limit: number;
}, options?: { signal?: AbortSignal }): Promise<PaginatedResponse<StatusOverTimeRow>> {
  const payload = {
    startDate,
    endDate,
    widgets: [
      {
        widgetId: WIDGET_IDS.STATUS_OVER_TIME,
        queueMicrosoftIds: queueMicrosoftIds ?? undefined,
        agentMicrosoftIds: agentMicrosoftIds ?? undefined,
        groupIds: groupIds ?? undefined,
        previousStartDate,
        previousEndDate,
        page,
        limit
      }
    ]
  };
  const res = await axiosInstance.post<any>(`/widgets/status-over-time`, payload, { signal: options?.signal });
  // Normalize shapes as above
  const top = res?.data ?? {};
  const container = (top?.data && !Array.isArray(top.data)) ? top.data : (top?.result && !Array.isArray(top.result)) ? top.result : top;
  const rawData: any[] = Array.isArray(top?.data)
    ? top.data
    : Array.isArray(container?.data)
      ? container.data
      : [];
  const mappedData = (rawData || []).map((item: any): StatusOverTimeRow => {
    const { agentName, ...rest } = item ?? {};
    return {
      agent: agentName ?? item?.agent ?? '',
      agentName: agentName ?? item?.agent ?? '',
      ...rest
    };
  });
  const total = Number(container?.total ?? top?.total ?? 0);
  const resp: PaginatedResponse<StatusOverTimeRow> = {
    data: mappedData,
    total,
    page: Number(container?.page ?? top?.page ?? page ?? 1),
    limit: Number(container?.limit ?? top?.limit ?? limit ?? 10),
    totalPages: Number(container?.totalPages ?? top?.totalPages ?? Math.ceil((total || 0) / (Number(container?.limit ?? top?.limit ?? limit ?? 10))))
  };
  return resp;
}

export async function fetchKeyMetrics({
  start,
  end,
  previousStart,
  previousEnd,
  statusTrendStart,
  statusTrendEnd,
  offsetInMinutes,
  queueIds,
  agentIds,
  directAgentIds
}: {
  start: string;
  end: string;
  previousStart: string;
  previousEnd: string;
  statusTrendStart: string;
  statusTrendEnd: string;
  offsetInMinutes: number;
  queueIds: string[];
  agentIds: string[];
  directAgentIds: string[];
}, options?: { signal?: AbortSignal }) {
  const payload = {
    start,
    end,
    previousStart,
    previousEnd,
    statusTrendStart,
    statusTrendEnd,
    offsetInMinutes,
    queueIds,
    agentIds,
    directAgentIds
  };
  const response = await axiosInstance.post('/widgets/key-metrics', payload, { signal: options?.signal });
  return response.data;
}

export async function fetchKeyMetricsBulk({
  start,
  end,
  previousStart,
  previousEnd,
  statusTrendStart,
  statusTrendEnd,
  offsetInMinutes,
  queueIds,
  agentIds,
  directAgentIds
}: {
  start: string;
  end: string;
  previousStart: string;
  previousEnd: string;
  statusTrendStart: string;
  statusTrendEnd: string;
  offsetInMinutes: number;
  queueIds: string[];
  agentIds: string[];
  directAgentIds: string[];
}, widgets: number[], options?: { signal?: AbortSignal }): Promise<Record<string, unknown>> {
  const payload = {
    parameters: {
      start,
      end,
      previousStart,
      previousEnd,
      statusTrendStart,
      statusTrendEnd,
      offsetInMinutes,
      queueIds,
      agentIds,
      directAgentIds
    },
    widgets,
    version: 2
  };
  const response = await axiosInstance.post('/widgets/key-metrics/bulk', payload, { signal: options?.signal });
  return response.data as Record<string, unknown>;
}

export async function fetchKeyMetricWidget({
  start,
  end,
  previousStart,
  previousEnd,
  statusTrendStart,
  statusTrendEnd,
  offsetInMinutes,
  queueIds,
  agentIds,
  directAgentIds
}: {
  start: string;
  end: string;
  previousStart: string;
  previousEnd: string;
  statusTrendStart: string;
  statusTrendEnd: string;
  offsetInMinutes: number;
  queueIds: string[];
  agentIds: string[];
  directAgentIds: string[];
}, widget: number, options?: { signal?: AbortSignal }) {
  const payload = {
    start,
    end,
    previousStart,
    previousEnd,
    statusTrendStart,
    statusTrendEnd,
    offsetInMinutes,
    queueIds,
    agentIds,
    directAgentIds
  };
  const response = await axiosInstance.post(`/widgets/key-metrics?widget=${widget}`, payload, { signal: options?.signal });
  return response.data;
}

export async function fetchKeyMetricsBulkRealtime({
  start,
  end,
  previousStart,
  previousEnd,
  offsetInMinutes,
  queueIds,
  agentIds,
  directAgentIds
}: {
  start: string;
  end: string;
  previousStart: string;
  previousEnd: string;
  offsetInMinutes: number;
  // null = no restriction (all queues/agents permitted); [] = zero permitted, matches nothing.
  // The Call Volume Trend widget (id 14) enforces this distinction server-side — do not
  // coalesce null to [] when building this payload (TCX-CVT_002).
  queueIds: string[] | null;
  agentIds: string[] | null;
  directAgentIds: string[] | null;
}, widgets: number[], options?: { signal?: AbortSignal }): Promise<Record<string, unknown>> {
  const payload = {
    parameters: {
      start,
      end,
      previousStart,
      previousEnd,
      offsetInMinutes,
      queueIds,
      agentIds,
      directAgentIds
    },
    widgets,
  };
  const response = await realtimeAxiosInstance.post('/widgets/key-metrics', payload, { signal: options?.signal });
  return response.data as Record<string, unknown>;
}

export interface InboundCallStatusDistributionData {
  answeredCall: number;
  missedCall: number;
  totalAbandonedCalls: number;
  total: number;
}

const QUEUE_INBOUND_STATUS_DISTRIBUTION_WIDGET_ID = 3;

// TCX-6287 Queue tab: per-queue Call Volume widget (widgetId=1 on POST /queue/widgets).
// TCX-4302: callId arrays (per TCX-5782 contract) let each stat tile drill into Call Journey.
export interface QueueCallVolumeStats {
  totalInboundCalls: number;
  answered: number;
  missed: number;
  transferred: number;
  abandoned: number;
  totalCallIdOffered: string[];
  totalCallIdOfferedAnswered: string[];
  totalCallIdOfferedMissed: string[];
  totalCallIdOfferedAbandoned: string[];
  totalCallIdOfferedTransferred: string[];
}

function pickQueueMetric(payload: Record<string, unknown>, keys: string[]): number {
  for (const key of keys) {
    if (payload[key] != null) {
      const n = Number(payload[key]);
      if (Number.isFinite(n)) return n;
    }
  }
  return 0;
}

function pickQueueCallIds(payload: Record<string, unknown>, key: string): string[] {
  const value = payload[key];
  return Array.isArray(value) ? value.map((v) => String(v)) : [];
}

export async function fetchQueueCallVolumeWidget(
  { startTime, endTime, queueMicrosoftId }: { startTime: string; endTime: string; queueMicrosoftId: string },
  options?: { signal?: AbortSignal }
): Promise<QueueCallVolumeStats> {
  const res = await realtimeAxiosInstance.post<any>(
    '/queue/widgets',
    { startTime, endTime, items: [{ widgetId: 1, queueMicrosoftIds: [queueMicrosoftId] }] },
    { signal: options?.signal }
  );
  const items: any[] = res?.data?.items ?? [];
  const payload = (items.find((i) => i.widgetId === 1)?.payload ?? {}) as Record<string, unknown>;
  return {
    totalInboundCalls: pickQueueMetric(payload, ['totalOffered', 'totalInboundCalls']),
    answered: pickQueueMetric(payload, ['answered', 'answeredCalls']),
    missed: pickQueueMetric(payload, ['missed', 'missedCalls']),
    transferred: pickQueueMetric(payload, ['transferred', 'transferredCalls']),
    abandoned: pickQueueMetric(payload, ['abandoned', 'abandonedCalls']),
    totalCallIdOffered: pickQueueCallIds(payload, 'totalCallIdOffered'),
    totalCallIdOfferedAnswered: pickQueueCallIds(payload, 'totalCallIdOfferedAnswered'),
    totalCallIdOfferedMissed: pickQueueCallIds(payload, 'totalCallIdOfferedMissed'),
    totalCallIdOfferedAbandoned: pickQueueCallIds(payload, 'totalCallIdOfferedAbandoned'),
    totalCallIdOfferedTransferred: pickQueueCallIds(payload, 'totalCallIdOfferedTransferred'),
  };
}

// TCX-6287 Queue tab: per-queue Call Journey table (tableId=1 on POST /queue/table) and its
// column-filter distinct values (filterId=1 on POST /queue/filter) — same contract as Direct Call's
// /direct/table + /direct/filter, scoped by queueMicrosoftIds instead of agentIds.
export async function fetchQueueCallJourneyTable(params: {
  startTime: string;
  endTime: string;
  queueMicrosoftId: string;
  pageIndex?: number;
  pageSize?: number | null;
  sorts?: { column: string; direction: 'Asc' | 'Desc' }[] | null;
  filters?: { column: string; values: string[] }[] | null;
  mainFilters?: { column: string; value: string }[] | null;
  callIds?: string[];
}, options?: { signal?: AbortSignal }): Promise<{ data: DirectCallJourneyRow[]; total: number; customerCompanyNameEnabled: boolean }> {
  const payload = {
    startTime: params.startTime,
    endTime: params.endTime,
    timeZone: getClientTimeZone(),
    items: [{
      tableId: 1,
      queueMicrosoftIds: [params.queueMicrosoftId],
      ...(params.callIds?.length ? { callIds: params.callIds } : {}),
      pageIndex: params.pageIndex ?? 0,
      // `??` would also collapse `pageSize: null` (used by callers to mean "fetch all") into
      // 10, so keep this explicit.
      pageSize: params.pageSize === undefined ? 10 : params.pageSize, // NOSONAR(typescript:S6606)
      mainFilters: params.mainFilters ?? null,
      filters: params.filters ?? null,
      sorts: params.sorts ?? [{ column: 'startTime', direction: 'Desc' }],
    }],
  };
  const res = await realtimeAxiosInstance.post<any>('/queue/table', payload, { signal: options?.signal });
  const raw: any[] = Array.isArray(res.data?.data) ? res.data.data : [];
  const rows: DirectCallJourneyRow[] = raw.map((item: any) => ({
    id: String(item.id ?? ''),
    startTime: String(item.startTime ?? ''),
    organizer: String(item.organizer ?? ''),
    participant: String(item.participant ?? ''),
    direction: String(item.direction ?? ''),
    callStatus: String(item.callStatus ?? ''),
    waitingDuration: Number(item.waitingDuration ?? 0),
    talkingDuration: Number(item.talkingDuration ?? 0),
    callDuration: Number(item.callDuration ?? 0),
    customerName: typeof item.customerName === 'string' && item.customerName ? item.customerName : null,
    companyName: typeof item.companyName === 'string' && item.companyName ? item.companyName : null,
    voicemail: Boolean(item.voicemail),
    journey: Array.isArray(item.journey) ? item.journey : [],
  }));
  const metadata = (res.data?.metadata ?? {}) as Record<string, unknown>;
  const customerCompanyNameEnabled = metadata.customerCompanyNameEnabled === true;
  return { data: rows, total: Number(res.data?.total ?? rows.length), customerCompanyNameEnabled };
}

export async function fetchQueueCallJourneyFilter(params: {
  startTime: string;
  endTime: string;
  queueMicrosoftId: string;
  targetColumn: string;
  pageIndex?: number;
  pageSize?: number;
  mainFilters?: { column: string; value: string }[] | null;
  // The filter endpoint only understands single-value `{ column, value }` clauses — `value` is
  // the search/filter text for that column (no `values` arrays).
  filters?: { column: string; value?: string | null }[] | null;
  callIds?: string[];
}, options?: { signal?: AbortSignal }): Promise<{ values: string[]; total: number }> {
  const payload = {
    startTime: params.startTime,
    endTime: params.endTime,
    timeZone: getClientTimeZone(),
    items: [{
      filterId: 1,
      queueMicrosoftIds: [params.queueMicrosoftId],
      ...(params.callIds?.length ? { callIds: params.callIds } : {}),
      mainFilters: params.mainFilters ?? null,
      pageIndex: params.pageIndex ?? 0,
      pageSize: params.pageSize ?? 500,
      filters: params.filters ?? null,
    }],
  };
  const res = await realtimeAxiosInstance.post<any>('/queue/filter', payload, { signal: options?.signal });
  const dataArr: any[] = Array.isArray(res.data?.data) ? res.data.data : [];
  const entry = dataArr.find((d: any) => d.column === params.targetColumn);
  const values: string[] = Array.isArray(entry?.value) ? entry.value.map((v: unknown) => String(v)) : [];
  const total: number = typeof entry?.total === 'number' ? entry.total : values.length;
  return { values, total };
}

export interface QueueCallVolumeSeriesData {
  intervalMinutes: number;
  labels: string[];
  series: Array<{ key: string; values: number[] }>;
}

const QUEUE_CALL_VOLUME_SERIES_WIDGET_ID = 4;

// Queue tab: Queue Performance overview table (tableId=2 on POST /overview/table) and column
// filter distinct values (filterId=2 on POST /overview/filter).
export const OVERVIEW_QUEUE_PERFORMANCE_TABLE_ID = '2';
export const OVERVIEW_QUEUE_PERFORMANCE_FILTER_ID = '2';

export const OVERVIEW_QUEUE_PERFORMANCE_BE_COLUMNS = [
  'name',
  'totalCall',
  'answered',
  'missed',
  'abandoned',
  'transferred',
  'voicemail',
  'avgHandleTime',
  'avgCallsPerAgent',
] as const;

const QUEUE_PERF_UI_TO_BE: Record<string, string> = {
  queueName: 'name',
  totalOfferedCalls: 'totalCall',
  answeredCall: 'answered',
  missedCall: 'missed',
  abandonedCall: 'abandoned',
  transferredCall: 'transferred',
  voicemail: 'voicemail',
  averageHandleTime: 'avgHandleTime',
  averageCallsPerAgent: 'avgCallsPerAgent',
};

export interface QueuePerformanceTableRow {
  queueName: string;
  totalOfferedCalls: number;
  answeredCall: number;
  missedCall: number;
  abandonedCall: number;
  transferredCall: number;
  voicemail: number;
  averageHandleTime: string;
  averageCallsPerAgent: number;
}

type OverviewQueueFilterItem = { column: string; value: string | null };
type OverviewQueueTableFilterItem = { column: string; values: string[] };

function toQueuePerfBeColumn(uiOrBeColumn: string): string {
  return QUEUE_PERF_UI_TO_BE[uiOrBeColumn] ?? uiOrBeColumn;
}

function buildOverviewQueuePerfColumnFilters(
  columnFilters: Record<string, string[]>
): OverviewQueueTableFilterItem[] | null {
  const out = Object.entries(columnFilters)
    .filter(([, vals]) => vals.length > 0)
    .map(([col, vals]) => ({ column: toQueuePerfBeColumn(col), values: vals }));
  return out.length ? out : null;
}

function mapOverviewQueuePerformanceRow(item: Record<string, unknown>): QueuePerformanceTableRow {
  return {
    queueName: String(item.name ?? ''),
    totalOfferedCalls: Number(item.totalCall ?? 0),
    answeredCall: Number(item.answered ?? 0),
    missedCall: Number(item.missed ?? 0),
    abandonedCall: Number(item.abandoned ?? 0),
    transferredCall: Number(item.transferred ?? 0),
    voicemail: Number(item.voicemail ?? 0),
    averageHandleTime: String(item.avgHandleTime ?? '00:00:00'),
    averageCallsPerAgent: Number(item.avgCallsPerAgent ?? 0),
  };
}

function parseOverviewFilterColumnValues(entry: Record<string, unknown>): string[] {
  const raw = entry.value ?? entry.values;
  if (!Array.isArray(raw)) return [];
  return raw.map((v) => String(v));
}

function extractOverviewFilterData(res: { data?: unknown }): Array<Record<string, unknown>> {
  const body = (res.data ?? {}) as Record<string, unknown>;
  if (Array.isArray(body.data)) return body.data as Array<Record<string, unknown>>;
  const nested = body.items;
  if (Array.isArray(nested) && nested[0] && typeof nested[0] === 'object') {
    const first = nested[0] as Record<string, unknown>;
    if (Array.isArray(first.data)) return first.data as Array<Record<string, unknown>>;
  }
  return [];
}

export async function fetchOverviewQueuePerformanceTable(params: {
  startTime: string;
  endTime: string;
  queueMicrosoftIds?: string[] | null;
  pageIndex?: number;
  pageSize?: number | null;
  sorts?: { column: string; direction: 'Asc' | 'Desc' }[] | null;
  filters?: Record<string, string[]> | null;
  mainFilters?: { column: string; value: string }[] | null;
}, options?: { signal?: AbortSignal }): Promise<{ data: QueuePerformanceTableRow[]; total: number }> {
  const sorts = params.sorts ?? [{ column: 'name', direction: 'Asc' as const }];
  const mappedSorts = sorts.map((s) => ({
    column: toQueuePerfBeColumn(s.column),
    direction: s.direction.toLowerCase() as 'asc' | 'desc',
  }));
  const payload = {
    startTime: params.startTime,
    endTime: params.endTime,
    items: [{
      tableId: OVERVIEW_QUEUE_PERFORMANCE_TABLE_ID,
      queueMicrosoftIds: params.queueMicrosoftIds ?? null,
      pageIndex: params.pageIndex ?? 0,
      // `??` would also collapse `pageSize: null` (used by callers to mean "fetch all") into
      // 10, so keep this explicit.
      pageSize: params.pageSize === undefined ? 10 : params.pageSize, // NOSONAR(typescript:S6606)
      mainFilters: params.mainFilters ?? null,
      filters: params.filters ? buildOverviewQueuePerfColumnFilters(params.filters) : null,
      sorts: mappedSorts,
    }],
  };
  const res = await realtimeAxiosInstance.post<any>('/overview/table', payload, { signal: options?.signal });
  const raw: Record<string, unknown>[] = Array.isArray(res.data?.data) ? res.data.data : [];
  return {
    data: raw.map((item) => mapOverviewQueuePerformanceRow(item)),
    total: Number(res.data?.total ?? raw.length),
  };
}

export async function fetchOverviewQueuePerformanceFilter(params: {
  startTime: string;
  endTime: string;
  queueMicrosoftIds?: string[] | null;
  targetColumns: string[];
  pageIndex?: number;
  pageSize?: number;
  mainFilters?: { column: string; value: string }[] | null;
  filters?: OverviewQueueFilterItem[] | null;
}, options?: { signal?: AbortSignal }): Promise<Record<string, { values: string[]; total: number }>> {
  const payload = {
    startTime: params.startTime,
    endTime: params.endTime,
    items: [{
      filterId: OVERVIEW_QUEUE_PERFORMANCE_FILTER_ID,
      queueMicrosoftIds: params.queueMicrosoftIds ?? null,
      mainFilters: params.mainFilters ?? null,
      pageIndex: params.pageIndex ?? 0,
      pageSize: params.pageSize ?? 50,
      filters: params.filters ?? params.targetColumns.map((column) => ({ column, value: null })),
    }],
  };
  const res = await realtimeAxiosInstance.post<any>('/overview/filter', payload, { signal: options?.signal });
  const dataArr = extractOverviewFilterData(res);
  const result: Record<string, { values: string[]; total: number }> = {};
  for (const entry of dataArr) {
    const column = String(entry.column ?? '');
    if (!column) continue;
    const values = parseOverviewFilterColumnValues(entry);
    result[column] = {
      values,
      total: typeof entry.total === 'number' ? entry.total : values.length,
    };
  }
  return result;
}

function resolveLastCallTaken(item: any): string | null {
  if (item.lastCallTakenAt) return String(item.lastCallTakenAt);
  if (item.lastCallTaken) return String(item.lastCallTaken);
  return null;
}

export async function fetchUserPerformanceTable(params: {
  startTime: string;
  endTime: string;
  agentMicrosoftIds: string[] | null;
  queueMicrosoftIds?: string[] | null;
  agentNameSearchText?: string | null;
  sortTable?: { columnName: string; columnValue: 'asc' | 'desc' } | null;
  page?: number;
  limit?: number | null;
  columnFilters?: { column: string; values: string[] }[] | null;
}, options?: { signal?: AbortSignal }): Promise<{ data: UserPerformanceTableRow[]; total: number }> {
  const pageIndex = Math.max(0, (params.page ?? 1) - 1);
  // `??` would also collapse `limit: null` (used by callers to mean "fetch all") into 10, so keep this explicit.
  const pageSize = params.limit === undefined ? 10 : params.limit; // NOSONAR(typescript:S6606)
  const sorts = params.sortTable
    ? [{ column: USER_TABLE_COL[params.sortTable.columnName] ?? params.sortTable.columnName, direction: params.sortTable.columnValue }]
    : null;
  const mainFilters = params.agentNameSearchText
    ? [{ column: 'name', value: params.agentNameSearchText }]
    : null;
  const filters = params.columnFilters?.length
    ? params.columnFilters.map(f => ({ column: USER_TABLE_COL[f.column] ?? f.column, values: f.values }))
    : null;
  const payload = {
    startTime: params.startTime,
    endTime: params.endTime,
    items: [{
      tableId: 1,
      queueMicrosoftIds: params.queueMicrosoftIds ?? null,
      agentIds: params.agentMicrosoftIds ?? [],
      mainFilters,
      filters,
      sorts,
      pageIndex,
      pageSize,
    }],
  };
  const res = await realtimeAxiosInstance.post<any>('/user/table', payload, { signal: options?.signal });
  const raw: any[] = Array.isArray(res.data?.data) ? res.data.data : [];
  const rows: UserPerformanceTableRow[] = raw.map((item: any) => ({
    userName: String(item.name ?? item.userName ?? ''),
    totalOffered: Number(item.totalOffered ?? 0),
    aht: Number(item.ahtSeconds ?? item.aht ?? 0),
    transferredPct: (item.transferRate ?? item.transferredPct) != null ? Number(item.transferRate ?? item.transferredPct) : null,
    sla: item.slaPercent != null ? Number(item.slaPercent) : null,
    lastCallTaken: resolveLastCallTaken(item),
  }));
  return { data: rows, total: Number(res.data?.total ?? rows.length) };
}

export interface AgentStatus {
  agentId: string;
  agentName: string;
  availability: string;
  activity: string;
  updatedAt: string;
  activeCallQueues: string[];
  activeCallQueueIds: string[];
  queueIds: string[];
  isDirectAgent: boolean;
  statusMessage: string | null;
  outOfOfficeMessage: string | null;
  directLineNumber: string | null;
  userPrincipalName: string;
}

export async function fetchAgentsStatus(options?: { signal?: AbortSignal }): Promise<AgentStatus[]> {
  const response = await realtimeAxiosInstance.get<AgentStatus[]>('/agents/status', { signal: options?.signal });
  return response.data;
}

export async function fetchUserPerformanceColumnFilter(params: {
  startTime: string;
  endTime: string;
  agentMicrosoftIds: string[] | null;
  queueMicrosoftIds?: string[] | null;
  targetColumn: string;
  searchValue?: string | null;
  pageIndex: number;
  pageSize: number;
  agentNameSearchText?: string | null;
  columnFilters?: { column: string; values: string[] }[] | null;
}, options?: { signal?: AbortSignal }): Promise<{ values: string[]; hasMore: boolean }> {
  const beColumn = USER_TABLE_COL[params.targetColumn] ?? params.targetColumn;
  const mainFilters = params.agentNameSearchText
    ? [{ column: 'name', value: params.agentNameSearchText }]
    : null;
  const otherFilters = params.columnFilters?.length
    ? params.columnFilters.map(f => ({ column: USER_TABLE_COL[f.column] ?? f.column, value: f.values }))
    : null;
  const payload = {
    startTime: params.startTime,
    endTime: params.endTime,
    items: [{
      filterId: 1,
      queueMicrosoftIds: params.queueMicrosoftIds ?? null,
      agentIds: params.agentMicrosoftIds ?? [],
      mainFilters,
      filters: [
        { column: beColumn, value: params.searchValue ?? null },
        ...(otherFilters ?? []),
      ],
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
    }],
  };
  const res = await realtimeAxiosInstance.post<any>('/user/filter', payload, { signal: options?.signal });
  const raw = res.data ?? {};
  // BE returns: { data: [{ column, value: string[], total }] }
  const dataArr: any[] = Array.isArray(raw.data) ? raw.data : [];
  const entry = dataArr.find((d: any) => d.column === beColumn) ?? dataArr[0];
  const values: string[] = Array.isArray(entry?.value) ? entry.value : [];
  const total: number = typeof entry?.total === 'number' ? entry.total : values.length;
  const hasMore = (params.pageIndex + 1) * params.pageSize < total;
  return { values, hasMore };
}

// ─── Agent detail popup (User overview → click a user) ─────────────────────
//
// Uses the same startTime/endTime the Users tab table itself is scoped to
// (passed down from UsersTab's dateRange), not an independent window.

function asStr(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function asNum(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function normalizeAgentPerformance(raw: unknown): AgentPerformance {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    handledCount: asNum(r.handledCount),
    missedCount: asNum(r.missedCount),
    ahtSeconds: asNum(r.ahtSeconds),
    transferRate: asNum(r.transferRate),
    slaPercent: typeof r.slaPercent === 'number' && Number.isFinite(r.slaPercent) ? r.slaPercent : null,
    lastCallTakenAt: typeof r.lastCallTakenAt === 'string' && r.lastCallTakenAt ? r.lastCallTakenAt : null,
    callIds: Array.isArray(r.callIds) ? (r.callIds as string[]) : [],
  };
}

export async function fetchAgentPerformance(opts: {
  agentId: string;
  startTime: string;
  endTime: string;
  queueMicrosoftIds?: string[] | null;
  signal?: AbortSignal;
}): Promise<AgentPerformance> {
  const body = {
    startTime: opts.startTime,
    endTime: opts.endTime,
    items: [{ tableId: '1', agentIds: [opts.agentId], queueMicrosoftIds: opts.queueMicrosoftIds ?? undefined }],
  };
  const { data } = await realtimeAxiosInstance.post<any>('/user/table', body, { signal: opts.signal });
  const items = Array.isArray(data?.data) ? data.data : [];
  return normalizeAgentPerformance(items[0] ?? {});
}

function normalizeAgentPerQueueRow(raw: unknown): AgentPerQueueRow {
  const r = (raw ?? {}) as Record<string, unknown>;
  const sla = typeof r.sla === 'number' && Number.isFinite(r.sla) ? r.sla : null;
  return {
    name: asStr(r.queue),
    line: null,
    offered: asNum(r.offered),
    handled: asNum(r.handled),
    missed: asNum(r.missed),
    transferred: asNum(r.transferred),
    sla,
    ahtSec: asNum(r.aht),
  };
}

export interface AgentPerQueueBreakdownResult {
  queues: AgentPerQueueRow[];
  totalHandled: number;
  totalMissed: number;
  totalOffered: number;
}

export async function fetchAgentPerQueueBreakdown(opts: {
  agentId: string;
  startTime: string;
  endTime: string;
  queueMicrosoftIds?: string[] | null;
  signal?: AbortSignal;
}): Promise<AgentPerQueueBreakdownResult> {
  const body = {
    startTime: opts.startTime,
    endTime: opts.endTime,
    items: [{ tableId: 3, agentIds: [opts.agentId], queueMicrosoftIds: opts.queueMicrosoftIds ?? undefined, pageIndex: 0, pageSize: null }],
  };
  const { data } = await realtimeAxiosInstance.post<any>('/user/table', body, { signal: opts.signal });
  let items: any[] = [];
  if (Array.isArray(data?.items)) items = data.items;
  else if (Array.isArray(data?.data)) items = data.data;
  const metadata = (data?.metadata ?? {}) as Record<string, unknown>;
  return {
    queues: items.map(normalizeAgentPerQueueRow),
    totalHandled: asNum(metadata.totalHandled),
    totalMissed: asNum(metadata.totalMissed),
    totalOffered: asNum(metadata.totalOffered),
  };
}

function normalizeAgentJourneyEvent(raw: unknown): AgentJourneyEvent {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    id: typeof r.id === 'number' ? r.id : 0,
    eventType: asStr(r.eventType),
    names: typeof r.names === 'string' ? r.names : null,
    startedAt: asStr(r.startedAt),
    inPeriodId: typeof r.inPeriodId === 'number' ? r.inPeriodId : null,
    inPeriodType: typeof r.inPeriodType === 'string' ? r.inPeriodType : null,
    inPeriodDuration:
      typeof r.inPeriodDuration === 'number' && Number.isFinite(r.inPeriodDuration) ? r.inPeriodDuration : null,
  };
}

function normalizeAgentCallDetail(raw: unknown): AgentCallDetail {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    startTime: asStr(r.startTime),
    direction: asStr(r.direction),
    organizer: asStr(r.organizer),
    participant: asStr(r.participant),
    callDuration: asNum(r.callDuration),
    voicemail: Boolean(r.voicemail),
    journey: Array.isArray(r.journey) ? r.journey.map(normalizeAgentJourneyEvent) : [],
    cqDirectline: typeof r.callQueue === 'string' ? r.callQueue : undefined,
    waitingDuration: asNum(r.waitingDuration),
    talkingDuration: asNum(r.talkingDuration),
    customerName: typeof r.customerName === 'string' && r.customerName ? r.customerName : null,
    companyName: typeof r.companyName === 'string' && r.companyName ? r.companyName : null,
  };
}

export type AgentCallSortClause = { column: string; direction: 'Asc' | 'Desc' };
export type AgentCallFilterClause = { column: string; values: string[] | null };

export async function fetchAgentCallDetails(opts: {
  callIds: string[];
  pageIndex: number;
  pageSize: number | null;
  sorts?: AgentCallSortClause[] | null;
  filters?: AgentCallFilterClause[] | null;
  startTime: string;
  endTime: string;
  signal?: AbortSignal;
}): Promise<AgentCallDetailsPage> {
  const body = {
    startTime: opts.startTime,
    endTime: opts.endTime,
    timeZone: getClientTimeZone(),
    items: [{
      tableId: '2',
      callIds: opts.callIds,
      pageIndex: opts.pageIndex,
      pageSize: opts.pageSize,
      sorts: opts.sorts ?? null,
      filters: opts.filters ?? null,
    }],
  };
  const { data } = await realtimeAxiosInstance.post<any>('/user/table', body, { signal: opts.signal });
  const items = Array.isArray(data?.data) ? data.data : [];
  const total = typeof data?.total === 'number' && Number.isFinite(data.total) ? data.total : items.length;
  const metadata = (data?.metadata ?? {}) as Record<string, unknown>;
  const customerCompanyNameEnabled = metadata.customerCompanyNameEnabled === true;
  return { calls: items.map(normalizeAgentCallDetail), total, customerCompanyNameEnabled };
}

const AGENT_CALL_JOURNEY_FILTER_PAGE_SIZE = 500;

export async function fetchAgentCallJourneyFilterDistinctValues(opts: {
  callIds: string[];
  targetApiColumn: string;
  distinctSearch: string;
  appliedApiFilters: ReadonlyArray<{ column: string; values: readonly string[] | null }>;
  startTime: string;
  endTime: string;
  pageIndex?: number;
  signal?: AbortSignal;
}): Promise<{ values: string[]; hasMore: boolean }> {
  const { startTime, endTime } = opts;
  const filters: Array<{ column: string; value: string | null }> = [];
  for (const { column, values } of opts.appliedApiFilters) {
    filters.push({ column, value: values && values.length > 0 ? values.join(', ') : null });
  }
  filters.push({ column: opts.targetApiColumn, value: opts.distinctSearch.trim() || null });
  const body = {
    startTime,
    endTime,
    timeZone: getClientTimeZone(),
    items: [{
      filterId: '2',
      callIds: opts.callIds,
      mainFilters: null,
      filters,
      pageIndex: opts.pageIndex ?? 0,
      pageSize: AGENT_CALL_JOURNEY_FILTER_PAGE_SIZE,
    }],
  };
  const { data } = await realtimeAxiosInstance.post<any>('/user/filter', body, { signal: opts.signal });
  const arr: any[] = Array.isArray(data?.data) ? data.data : [];
  const want = opts.targetApiColumn.trim().toLowerCase();
  const match = arr.find((x) => String(x?.column ?? x?.Column ?? '').trim().toLowerCase() === want);
  if (!match || !Array.isArray(match.value)) return { values: [], hasMore: false };
  const values = (match.value as unknown[])
    .map((x) => (x === null || x === undefined ? '' : String(x)))
    .filter((s) => s.trim() !== '');
  return { values, hasMore: values.length >= AGENT_CALL_JOURNEY_FILTER_PAGE_SIZE };
}

export interface AgentModalKpiWidgets {
  voicemail: number;
  internal: number;
}

export async function fetchAgentModalKpiWidgets(opts: {
  agentId: string;
  startTime: string;
  endTime: string;
  queueMicrosoftIds?: string[] | null;
  signal?: AbortSignal;
}): Promise<AgentModalKpiWidgets> {
  const res = await realtimeAxiosInstance.post<any>(
    '/user/widgets',
    {
      startTime: opts.startTime,
      endTime: opts.endTime,
      items: [
        { widgetId: 7, agentIds: [opts.agentId], queueMicrosoftIds: opts.queueMicrosoftIds ?? undefined },
        { widgetId: 8, agentIds: [opts.agentId], queueMicrosoftIds: opts.queueMicrosoftIds ?? undefined },
      ],
    },
    { signal: opts.signal },
  );
  const items: any[] = res?.data?.items ?? [];
  const voicemail = Number(items.find((i) => i.widgetId === 7)?.payload?.value ?? 0);
  const internal = Number(items.find((i) => i.widgetId === 8)?.payload?.value ?? 0);
  return { voicemail, internal };
}

const QUEUE_INBOUND_HANDLED_BY_QUEUE_WIDGET_ID = 2;

export interface QueueOverviewWidgetsBatchResult {
  handledByQueue: InboundHandledByQueueRow[];
  inboundDistribution: InboundCallStatusDistributionData;
  /** Null when widgetId 4 was skipped because `queueMicrosoftIds` was empty. */
  callVolumeSeries: QueueCallVolumeSeriesData | null;
}

// Queue tab: batch the 3 tab-level widgets (widgetId 2, 3, 4) into one POST /queue/widgets per
// refresh cycle. Widget 2 (InboundHandledByQueue) accepts empty queueMicrosoftIds (= all queues);
// the backend rejects widgetId 3/4 with 400 when queueMicrosoftIds is empty (see
// ReportEndpoints.cs), so those items are only included when ids exist.
export async function fetchQueueOverviewWidgetsBatch({
  startDate,
  endDate,
  handledQueueMicrosoftIds,
  queueMicrosoftIds,
}: {
  startDate: string;
  endDate: string;
  /** Raw queue selection for widgetId 2; null/[] means all queues. */
  handledQueueMicrosoftIds?: string[] | null;
  /** Resolved queue ids scoping widgetId 3 and 4; [] skips both. */
  queueMicrosoftIds: string[];
}, options?: { signal?: AbortSignal }): Promise<QueueOverviewWidgetsBatchResult> {
  const includeQueueScoped = queueMicrosoftIds.length > 0;
  const items: Array<Record<string, unknown>> = [{
    widgetId: QUEUE_INBOUND_HANDLED_BY_QUEUE_WIDGET_ID,
    queueMicrosoftIds: handledQueueMicrosoftIds ?? [],
    agentIds: [],
  }];
  if (includeQueueScoped) {
    items.push(
      { widgetId: QUEUE_INBOUND_STATUS_DISTRIBUTION_WIDGET_ID, queueMicrosoftIds },
      { widgetId: QUEUE_CALL_VOLUME_SERIES_WIDGET_ID, queueMicrosoftIds },
    );
  }
  const res = await realtimeAxiosInstance.post<any>(
    '/queue/widgets',
    { startTime: startDate, endTime: endDate, items },
    { signal: options?.signal }
  );
  const resItems: any[] = res?.data?.items ?? [];
  const getPayload = (id: number): Record<string, unknown> =>
    resItems.find((i) => i.widgetId === id)?.payload ?? {};

  const handledData = getPayload(QUEUE_INBOUND_HANDLED_BY_QUEUE_WIDGET_ID).data;
  const distribution = getPayload(QUEUE_INBOUND_STATUS_DISTRIBUTION_WIDGET_ID);
  const series = getPayload(QUEUE_CALL_VOLUME_SERIES_WIDGET_ID);

  return {
    handledByQueue: Array.isArray(handledData) ? handledData : [],
    inboundDistribution: {
      answeredCall: Number(distribution.answeredCall ?? 0),
      missedCall: Number(distribution.missedCall ?? 0),
      totalAbandonedCalls: Number(distribution.totalAbandonedCalls ?? 0),
      total: Number(distribution.total ?? 0),
    },
    callVolumeSeries: includeQueueScoped
      ? {
          intervalMinutes: Number(series.intervalMinutes ?? 0),
          labels: Array.isArray(series.labels) ? (series.labels as string[]) : [],
          series: Array.isArray(series.series)
            ? (series.series as Array<{ key: string; values: number[] }>)
            : [],
        }
      : null,
  };
}

// ─── Direct Call tab ────────────────────────────────────────────────────────

export interface DirectCallStatusSummary {
  answeredCall: number;
  missedCall: number;
}

export interface DirectCallVolumeSeries {
  intervalMinutes: number;
  labels: string[];
  series: { key: 'Inbound' | 'Outbound' | 'Inbound Direct' | 'Internal'; values: number[] }[];
}

export interface DirectWidgetsBatchResult {
  outboundStatus: DirectCallStatusSummary;
  inboundDirectStatus: DirectCallStatusSummary;
  callVolume: DirectCallVolumeSeries;
}

function mapDirectSegments(payload: any): DirectCallStatusSummary {
  const segments: any[] = Array.isArray(payload?.segments) ? payload.segments : [];
  const answeredCall = Number(segments.find((s) => s.label === 'Answered')?.count ?? 0);
  const missedCall = Number(segments.find((s) => s.label === 'Missed')?.count ?? 0);
  return { answeredCall, missedCall };
}

// Direct Call tab: batch fetch the 3 widgets in one request (widgetIds 3, 4, 5)
/**
 * TCX-6503: a Manager's role scope — the call queues and agents their PortalCX role grants.
 * Travels at request level and narrows which calls exist. It is NOT the manual filter: only the
 * per-item `agentIds` switches a widget's calculation rule (whole-call vs offered/hunt-leg).
 * Admin/Partner send `[]` for every dimension; a Manager with no permitted queues or groups sends
 * the reserved sentinel id so an empty scope cannot be read as "no restriction".
 */
export interface DirectRoleScope {
  queueIds: string[];
  agentIds: string[];
  directAgentIds: string[];
}

/** All dimensions empty = no role restriction. What Admin/Partner send, and the default. */
const NO_ROLE_SCOPE: DirectRoleScope = { queueIds: [], agentIds: [], directAgentIds: [] };

/**
 * `allAgentsSelected: true` tells the backend "show every agent in scope" — never a truthful
 * signal for a Manager whose role scope is genuinely empty. DirectCallTab substitutes the
 * reserved EMPTY_AGENT_ID sentinel for exactly that case (an empty `agentIds` array would
 * otherwise be misread as "no restriction"); a manual selection of nothing on top of a
 * zero-agent scope must resolve to "nothing", not "all". `items[].agentIds` gets the same
 * sentinel as the outer role scope here too — a bare `[]` alongside `allAgentsSelected: false`
 * is still an empty array a less careful backend path could misread as "unrestricted"; matching
 * the outer scope's non-existent id is unambiguous regardless of which field a given code path
 * actually looks at.
 */
function resolveDirectAgentSelection(
  roleScope: DirectRoleScope | undefined,
  manualAgentIds: string[] | null | undefined
): { agentIds: string[]; allAgentsSelected: boolean } {
  const scope = roleScope ?? NO_ROLE_SCOPE;
  const isZeroAgentScope = scope.agentIds.length === 1 && scope.agentIds[0] === EMPTY_AGENT_ID;
  if (isZeroAgentScope) {
    return { agentIds: scope.agentIds, allAgentsSelected: false };
  }
  const manual = manualAgentIds ?? [];
  return { agentIds: manual, allAgentsSelected: manual.length === 0 };
}

export async function fetchDirectWidgetsBatch({
  startDate,
  endDate,
  roleScope,
  manualAgentIds,
}: {
  startDate: string;
  endDate: string;
  roleScope?: DirectRoleScope;
  manualAgentIds?: string[] | null;
}, options?: { signal?: AbortSignal }): Promise<DirectWidgetsBatchResult> {
  // TCX-6503: state the intent instead of letting the server infer it from an empty array.
  // No specific agents picked = the user is on "All" — unless their role scope has zero agents
  // to begin with (see resolveDirectAgentSelection).
  const { agentIds: itemAgentIds, allAgentsSelected } = resolveDirectAgentSelection(roleScope, manualAgentIds);
  const scope = roleScope ?? NO_ROLE_SCOPE;
  const res = await realtimeAxiosInstance.post<any>(
    '/direct/widgets',
    {
      startTime: startDate,
      endTime: endDate,
      // Role scope at request level; items[].agentIds stays the user's manual selection.
      queueIds: scope.queueIds,
      agentIds: scope.agentIds,
      directAgentIds: scope.directAgentIds,
      items: [
        { widgetId: 3, agentIds: itemAgentIds, allAgentsSelected },
        { widgetId: 4, agentIds: itemAgentIds, allAgentsSelected },
        { widgetId: 5, agentIds: itemAgentIds, allAgentsSelected },
      ],
    },
    { signal: options?.signal }
  );
  const items: any[] = res?.data?.items ?? [];
  const getPayload = (id: number) => items.find((i: any) => i.widgetId === id)?.payload ?? {};

  const p5 = getPayload(5);

  return {
    outboundStatus: mapDirectSegments(getPayload(3)),
    inboundDirectStatus: mapDirectSegments(getPayload(4)),
    callVolume: {
      intervalMinutes: Number(p5.intervalMinutes ?? 60),
      labels: Array.isArray(p5.labels) ? p5.labels : [],
      series: Array.isArray(p5.series) ? p5.series : [],
    },
  };
}

export interface JourneyEvent {
  id: number;
  eventType: string;
  names: string | null;
  startedAt: string;
  inPeriodId: number | null;
  inPeriodType: string | null;
  inPeriodDuration: number | null;
}

export interface DirectCallJourneyRow {
  id: string;
  startTime: string;
  organizer: string;
  participant: string;
  direction: string;
  callStatus: string;
  waitingDuration: number;
  talkingDuration: number;
  callDuration: number;
  /** Matched CRM customer name from the report API (`customerName`); null when unmatched. */
  customerName: string | null;
  /** Matched CRM company name from the report API (`companyName`); null when unmatched. */
  companyName: string | null;
  voicemail: boolean;
  journey: JourneyEvent[];
}

// Direct Call tab: Call Journey table (tableId=2)
export async function fetchDirectTable(params: {
  startTime: string;
  endTime: string;
  /** TCX-6503 role scope — request level, narrows which calls exist. Omitted = unrestricted. */
  roleScope?: DirectRoleScope;
  /** The user's manual filter selection only. Never the role scope. */
  manualAgentIds?: string[] | null;
  pageIndex?: number;
  pageSize?: number | null;
  sorts?: { column: string; direction: 'Asc' | 'Desc' }[] | null;
  filters?: { column: string; values: string[] }[] | null;
  mainFilters?: { column: string; value: string }[] | null;
}, options?: { signal?: AbortSignal }): Promise<{ data: DirectCallJourneyRow[]; total: number; customerCompanyNameEnabled: boolean }> {
  const { agentIds: itemAgentIds, allAgentsSelected } = resolveDirectAgentSelection(params.roleScope, params.manualAgentIds);
  const payload = {
    startTime: params.startTime,
    endTime: params.endTime,
    queueIds: (params.roleScope ?? NO_ROLE_SCOPE).queueIds,
    agentIds: (params.roleScope ?? NO_ROLE_SCOPE).agentIds,
    directAgentIds: (params.roleScope ?? NO_ROLE_SCOPE).directAgentIds,
    timeZone: getClientTimeZone(),
    items: [{
      tableId: 2,
      pageIndex: params.pageIndex ?? 0,
      // `??` would also collapse `pageSize: null` (used by callers to mean "fetch all") into
      // 10, so keep this explicit.
      pageSize: params.pageSize === undefined ? 10 : params.pageSize, // NOSONAR(typescript:S6606)
      agentIds: itemAgentIds,
      allAgentsSelected,
      mainFilters: params.mainFilters ?? null,
      filters: params.filters ?? null,
      sorts: params.sorts ?? [{ column: 'startTime', direction: 'Desc' }],
    }],
  };
  const res = await realtimeAxiosInstance.post<any>('/direct/table', payload, { signal: options?.signal });
  const raw: any[] = Array.isArray(res.data?.data) ? res.data.data : [];
  const rows: DirectCallJourneyRow[] = raw.map((item: any) => ({
    id: String(item.id ?? ''),
    startTime: String(item.startTime ?? ''),
    organizer: String(item.organizer ?? ''),
    participant: String(item.participant ?? ''),
    direction: String(item.direction ?? ''),
    callStatus: String(item.callStatus ?? ''),
    waitingDuration: Number(item.waitingDuration ?? 0),
    talkingDuration: Number(item.talkingDuration ?? 0),
    callDuration: Number(item.callDuration ?? 0),
    customerName: typeof item.customerName === 'string' && item.customerName ? item.customerName : null,
    companyName: typeof item.companyName === 'string' && item.companyName ? item.companyName : null,
    voicemail: Boolean(item.voicemail),
    journey: Array.isArray(item.journey) ? item.journey : [],
  }));
  const metadata = (res.data?.metadata ?? {}) as Record<string, unknown>;
  const customerCompanyNameEnabled = metadata.customerCompanyNameEnabled === true;
  return { data: rows, total: Number(res.data?.total ?? rows.length), customerCompanyNameEnabled };
}

// Direct Call tab: Call Journey column filter distinct values (filterId=2)
export async function fetchDirectFilter(params: {
  startTime: string;
  endTime: string;
  /** TCX-6503 role scope — must match what the table sends, or facets offer values with no rows. */
  roleScope?: DirectRoleScope;
  /** The user's manual filter selection only. Never the role scope. */
  manualAgentIds?: string[] | null;
  targetColumn: string;
  pageIndex?: number;
  pageSize?: number;
  mainFilters?: { column: string; value: string }[] | null;
  // The filter endpoint only understands single-value `{ column, value }` clauses — `value` is
  // the search/filter text for that column (no `values` arrays).
  filters?: { column: string; value?: string | null }[] | null;
}, options?: { signal?: AbortSignal }): Promise<{ values: string[]; total: number }> {
  const { agentIds: itemAgentIds, allAgentsSelected } = resolveDirectAgentSelection(params.roleScope, params.manualAgentIds);
  const payload = {
    startTime: params.startTime,
    endTime: params.endTime,
    queueIds: (params.roleScope ?? NO_ROLE_SCOPE).queueIds,
    agentIds: (params.roleScope ?? NO_ROLE_SCOPE).agentIds,
    directAgentIds: (params.roleScope ?? NO_ROLE_SCOPE).directAgentIds,
    timeZone: getClientTimeZone(),
    items: [{
      filterId: 2,
      agentIds: itemAgentIds,
      allAgentsSelected,
      mainFilters: params.mainFilters ?? null,
      pageIndex: params.pageIndex ?? 0,
      pageSize: params.pageSize ?? 500,
      filters: params.filters ?? null,
    }],
  };
  const res = await realtimeAxiosInstance.post<any>('/direct/filter', payload, { signal: options?.signal });
  const dataArr: any[] = Array.isArray(res.data?.data) ? res.data.data : [];
  const entry = dataArr.find((d: any) => d.column === params.targetColumn);
  const values: string[] = Array.isArray(entry?.value) ? entry.value.map((v: unknown) => String(v)) : [];
  const total: number = typeof entry?.total === 'number' ? entry.total : values.length;
  return { values, total };
}
